import dateFormat from 'dateformat';
import { Flex, Text } from '@chakra-ui/react';
import { faFile } from '@fortawesome/pro-regular-svg-icons';
import { Banner, OutlineCard, Table } from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { recommendationBadges } from '../Review/Badges';
import { ReviewFormFields } from '@publishing-engine/submission-service/src/workflow-configuration/review-fields/review-form-fields';
import {
  consentAccordionContent,
  optInAccordionContent,
  submissionScoreTableContent
} from './ReviewPreviewModal';
import { useReviewData } from 'src/contexts/ReviewDataProvider';
import { FormFieldRadio } from '@publishing-engine/domain/types/article';
import FileViewerButtonText from '../Submission/FileViewerButtonText';
import { DisplayField } from 'src/components/shared/DisplayField';

export const RevisionReviewedPage: React.FC<{}> = () => {
  const { review, revisionFormSchema, pastReviewsData } = useReviewData();
  const { workflow, caseData, submissionContextId } = review;
  const reviewedDate = workflow.states?.find(state => state.name === 'inReview')
    ?.completedWith?.date;

  let revisionAssessment;
  if (caseData.revisionAssessment) {
    revisionAssessment = caseData.revisionAssessment;
  } else if (caseData.revisionAssessmentHistory?.length) {
    revisionAssessment =
      caseData.revisionAssessmentHistory[
        caseData.revisionAssessmentHistory.length - 1
      ];
  }
  const firstReviewerFeedback = pastReviewsData?.previousReviews
    ?.flatMap(r => r.rounds)
    ?.flatMap(round => round.reviewerRevisionFeedback ?? [])
    ?.find(feedback => feedback.reviewAssessment);

  const peerReviewTransparency =
    firstReviewerFeedback?.reviewAssessment?.peerReviewTransparency;
  const reviewerAnonymity =
    firstReviewerFeedback?.reviewAssessment?.reviewerAnonymity;
  const reviewFormFields = new ReviewFormFields(
    revisionFormSchema.documentSchema
  );
  const configs: any = {
    Recommendation: reviewFormFields.getFieldConfig(
      'Recommendation'
    ) as unknown as FormFieldRadio,
    // submission scores
    Originality: reviewFormFields.getFieldConfig('Originality'),
    Significance: reviewFormFields.getFieldConfig('Significance'),
    Rigour: reviewFormFields.getFieldConfig('Rigour'),
    Clarity: reviewFormFields.getFieldConfig('Clarity'),
    Impact: reviewFormFields.getFieldConfig('Impact'),
    UnaddressedReviewComments: reviewFormFields.getFieldConfig(
      'UnaddressedReviewComments'
    ),
    // Opt Ins
    ReceiveFeedback: reviewFormFields.getFieldConfig('ReceiveFeedback'),
    WebOfScienceOptIn: reviewFormFields.getFieldConfig('WebOfScienceOptIn'),
    PeerReviewTransparency: reviewFormFields.getFieldConfig(
      'PeerReviewTransparency'
    ),
    ReviewerAnonymity: reviewFormFields.getFieldConfig('ReviewerAnonymity'),
    ConsentToReInvite: reviewFormFields.getFieldConfig('ConsentToReInvite')
  };

  return (
    <Flex flexDirection="column" gap={4} p={2}>
      <Banner
        content={
          <Text textStyle={'textContext'}>
            Your review was sent to the editors on{' '} 
            {dateFormat(reviewedDate, 'mmmm d, yyyy')}. You can only review the
            sent information.
          </Text>
        }
        heading="Your Review"
        variant="success"
      />
      {revisionAssessment?.recommendation && (
        <Flex flexDirection="column" gap={1}>
          <Text textStyle="headingSection">Recommendation</Text>
          <Flex>
            {recommendationBadges[revisionAssessment.recommendation] ||
              configs.Recommendation?.params?.labels?.options[
                revisionAssessment.recommendation
              ]?.main}
          </Flex>
        </Flex>
      )}
      {revisionAssessment?.submissionScores && (
        <OutlineCard p={4}>
          <Flex flexDirection="column" gap={6}>
            <Text textStyle="headingSection">Submission Scores</Text>
            <Flex flexDirection="column">
              <Table>
                <Table.HeaderRow padding={2}>
                  <Table.HeaderCell flex="1" textAlign="left">
                    Criteria
                  </Table.HeaderCell>
                  <Table.HeaderCell flex="1" textAlign="left">
                    Score
                  </Table.HeaderCell>
                </Table.HeaderRow>
                <Table.Body sx={{ borderTop: 'none' }}>
                  <>
                    {submissionScoreTableContent(revisionAssessment, configs)}
                  </>
                </Table.Body>
              </Table>
            </Flex>
          </Flex>
        </OutlineCard>
      )}

      {(revisionAssessment?.feedbackForAuthors?.original ||
        revisionAssessment?.reviewerAttachments?.length ||
        revisionAssessment?.missingReference?.exists) && (
        <OutlineCard p={4}>
          <Flex flexDirection="column" gap={6}>
            <Text textStyle="headingSection">For Authors</Text>
            {revisionAssessment?.feedbackForAuthors?.original && (
              <Flex flexDirection="column" gap={2}>
                <Text textStyle="headingItem">Feedback for Authors</Text>
                <Text textStyle="textBody">
                  {revisionAssessment.feedbackForAuthors.original}
                </Text>
              </Flex>
            )}
            {revisionAssessment?.reviewerAttachments?.length ? (
              <Flex flexDirection="column" gap={2}>
                <Text textStyle="headingItem">Review Attachments</Text>

                <Flex flexDirection="column" gap={2}>
                  {revisionAssessment.reviewerAttachments.map(
                    (attachment, index) => (
                      <Flex justifyContent="space-between" key={index}>
                        <Flex gap={2} alignItems="center">
                          <FontAwesomeIcon icon={faFile} />
                          <Text
                            textStyle="textBody"
                            whiteSpace={'normal'}
                            wordBreak={'break-word'}
                            textAlign={'initial'}
                          >
                            {attachment.name}
                          </Text>
                          <FileViewerButtonText
                            submissionContextId={submissionContextId}
                            file={attachment}
                          >
                            View
                          </FileViewerButtonText>
                        </Flex>
                      </Flex>
                    )
                  )}
                </Flex>
              </Flex>
            ) : null}
            {revisionAssessment?.missingReference?.value && (
              <Flex flexDirection="column" gap={2}>
                <Text textStyle="headingItem">
                  References to Recent/Appropriate Work
                </Text>
                <Text textStyle="textBody">
                  The author has not referenced the most recent and appropriate
                  work.
                </Text>
                <Text textStyle="textBody">
                  {revisionAssessment.missingReference.value}
                </Text>
              </Flex>
            )}
          </Flex>
        </OutlineCard>
      )}
      {(revisionAssessment?.betterSuitedForAnotherJournal?.exists ||
        revisionAssessment?.feedbackForEditors) && (
        <OutlineCard p={4}>
          <Flex flexDirection="column" gap={6}>
            <Text textStyle="headingSection">For Editors</Text>
            {configs?.UnaddressedReviewComments &&
              revisionAssessment &&
              'unaddressedReviewComments' in revisionAssessment &&
              revisionAssessment?.unaddressedReviewComments && (
                <DisplayField
                  fieldName="UnaddressedReviewComments"
                  value={revisionAssessment?.unaddressedReviewComments}
                  config={configs}
                  showAdditionalDetails={true}
                />
              )}
            {revisionAssessment?.betterSuitedForAnotherJournal?.exists && (
              <Flex flexDirection="column" gap={2}>
                <Text textStyle="headingItem">Submission Scope</Text>
                <Text textStyle="textBody">
                  This Submission is better suited to the scope of{' '}
                  {revisionAssessment.betterSuitedForAnotherJournal.value}
                </Text>
              </Flex>
            )}
            {revisionAssessment?.feedbackForEditors && (
              <Flex flexDirection="column" gap={2}>
                <Text textStyle="headingItem">Feedback for Editors</Text>
                <Text textStyle="textBody">
                  {revisionAssessment.feedbackForEditors}
                </Text>
              </Flex>
            )}
            {revisionAssessment?.reviewerAttachmentsEditor?.length ? (
              <Flex flexDirection="column" gap={2}>
                <Text textStyle="headingItem">
                  Review Attachments for Editor
                </Text>

                <Flex flexDirection="column" gap={2}>
                  {revisionAssessment.reviewerAttachmentsEditor.map(
                    (attachment, index) => (
                      <Flex justifyContent="space-between" key={index}>
                        <Flex gap={2} alignItems="center">
                          <FontAwesomeIcon icon={faFile} />
                          <Text
                            textStyle="textBody"
                            whiteSpace={'normal'}
                            wordBreak={'break-word'}
                            textAlign={'initial'}
                          >
                            {attachment.name}
                          </Text>
                          <FileViewerButtonText
                            submissionContextId={submissionContextId}
                            file={attachment}
                          >
                            View
                          </FileViewerButtonText>
                        </Flex>
                      </Flex>
                    )
                  )}
                </Flex>
              </Flex>
            ) : null}
          </Flex>
        </OutlineCard>
      )}
      <OutlineCard p={4}>
        <Flex flexDirection="column" gap={2}>
          <Text textStyle="headingSection">Opt Ins</Text>
          {revisionAssessment &&
            optInAccordionContent(
              revisionAssessment,
              configs,
              0,
              peerReviewTransparency,
              reviewerAnonymity
            )}
        </Flex>
      </OutlineCard>
      {revisionAssessment?.consentToReInvite && (
        <OutlineCard p={4}>
          <Flex flexDirection="column" gap={2}>
            <Text textStyle="headingSection">Consent</Text>
            {revisionAssessment &&
              consentAccordionContent(revisionAssessment, configs, 0)}
          </Flex>
        </OutlineCard>
      )}
    </Flex>
  );
};
